import { Fragment, useEffect, useMemo } from 'react'

import { Listbox, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { Check, ChevronDown, ImagePlus, X } from 'lucide-react'
import { Controller, type UseFormReturn } from 'react-hook-form'

import { UploadHint } from 'components/UploadHint'
import { useGame } from 'store/game/hooks'

import type { CreateTournamentInput } from '../types'
import { FOCUS_RING, FieldError, FieldLabel, Help, Section, inputClass } from '../ui'
import { useObjectUrl } from '../useObjectUrl'

const DESCRIPTION_SOFT_LIMIT = 2000

export const BasicsStep = ({ form }: { form: UseFormReturn<CreateTournamentInput> }) => {
  const {
    control,
    formState: { errors },
  } = form
  const { games, isLoading, getAllGames } = useGame()

  useEffect(() => {
    if (!games || games.length === 0) getAllGames()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <Section>
        <div className="grid gap-5">
          {/* Game */}
          <div data-field="game">
            <Controller
              control={control}
              name="game"
              rules={{ required: 'Choose the game this tournament is for.' }}
              render={({ field }) => (
                <GamePicker
                  games={games || []}
                  loading={isLoading}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.game?.message}
                />
              )}
            />
          </div>

          {/* Name */}
          <div data-field="tournament_name">
            <FieldLabel htmlFor="ct-name">Tournament name</FieldLabel>
            <Controller
              control={control}
              name="tournament_name"
              rules={{
                required: 'Give your tournament a name.',
                validate: (v) => (v && v.trim().length >= 3) || 'Use at least 3 characters for the name.',
              }}
              render={({ field }) => (
                <input
                  id="ct-name"
                  type="text"
                  autoComplete="off"
                  maxLength={80}
                  placeholder="e.g. Friday Night Cup #12"
                  aria-invalid={!!errors.tournament_name}
                  aria-describedby={errors.tournament_name ? 'ct-name-error' : 'ct-name-help'}
                  className={classNames(inputClass(!!errors.tournament_name), 'text-base font-semibold')}
                  name={field.name}
                  ref={field.ref}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  value={field.value || ''}
                />
              )}
            />
            {errors.tournament_name ? (
              <FieldError id="ct-name-error" message={errors.tournament_name.message} />
            ) : (
              <Help id="ct-name-help">Shown on the tournament card, page and invites.</Help>
            )}
          </div>
        </div>
      </Section>

      <Section title="Images" description="Optional. Without a banner we use a generated cover.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
          <Controller
            control={control}
            name="cover_photo"
            render={({ field }) => (
              <ImageDrop
                id="ct-cover"
                label="Banner"
                aspect="aspect-[4/1] sm:aspect-auto sm:h-36"
                file={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="avatar"
            render={({ field }) => (
              <ImageDrop
                id="ct-avatar"
                label="Logo"
                aspect="aspect-square w-28 sm:w-auto sm:h-36"
                file={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <UploadHint type="banner" />
          <UploadHint type="avatar" />
        </div>
      </Section>

      <Section title="Description" description="Rules, match format, check-in, anything players should know before they sign up.">
        <Controller
          control={control}
          name="tournament_description"
          render={({ field }) => (
            <div>
              <label htmlFor="ct-description" className="sr-only">
                Description
              </label>
              <textarea
                id="ct-description"
                rows={5}
                placeholder="e.g. 5v5, best of 3 maps. Check in 30 minutes before start in Discord. Standard competitive rules apply."
                className={classNames(inputClass(), 'resize-y leading-6')}
                name={field.name}
                ref={field.ref}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                value={field.value || ''}
              />
              <p
                className={classNames(
                  'mt-1 text-right text-[11px] tabular-nums',
                  (field.value?.length || 0) > DESCRIPTION_SOFT_LIMIT ? 'text-yellow' : 'text-light-typo-gray dark:text-dark-typo-gray',
                )}
                aria-live="polite"
              >
                {field.value?.length || 0} characters
              </p>
            </div>
          )}
        />
      </Section>
    </div>
  )
}

type GameOption = { _id: string; name: string; avatar?: { url?: string }; cover_photo?: { url?: string } }

const GamePicker = ({
  games,
  loading,
  value,
  onChange,
  error,
}: {
  games: GameOption[]
  loading: boolean
  value?: string
  onChange: (id: string) => void
  error?: string
}) => {
  const selected = useMemo(() => games.find((g) => g._id === value), [games, value])
  const img = (g: GameOption) => g.avatar?.url || g.cover_photo?.url

  return (
    <Listbox value={value || ''} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Label className="mb-1.5 block text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">Game</Listbox.Label>
          <Listbox.Button
            aria-invalid={!!error}
            className={classNames(inputClass(!!error), 'flex items-center justify-between gap-3 text-left', FOCUS_RING)}
          >
            <span className="flex min-w-0 items-center gap-3">
              {selected ? (
                <>
                  {img(selected) ? (
                    <img src={img(selected)} alt="" className="h-8 w-8 flex-shrink-0 rounded-md object-cover" />
                  ) : (
                    <span className="h-8 w-8 flex-shrink-0 rounded-md bg-dark-bg-secondary" />
                  )}
                  <span className="truncate text-base font-semibold text-light-typo-primary dark:text-dark-typo-primary">{selected.name}</span>
                </>
              ) : (
                <span className="py-1.5 text-light-typo-gray dark:text-dark-typo-gray">
                  {loading ? 'Loading games…' : games.length ? 'Choose a game' : 'No games available'}
                </span>
              )}
            </span>
            <ChevronDown
              size={18}
              aria-hidden
              className={classNames('flex-shrink-0 text-light-typo-gray dark:text-dark-typo-gray transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            />
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-75 motion-reduce:transition-none"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-card dark:bg-dark-card p-1 shadow-xl focus:outline-none">
              {games.map((game) => (
                <Listbox.Option
                  key={game._id}
                  value={game._id}
                  className={({ active }) =>
                    classNames(
                      'flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm',
                      active ? 'bg-light-gray-light dark:bg-dark-bg-secondary' : '',
                    )
                  }
                >
                  {({ selected: isSelected }) => (
                    <>
                      {img(game) ? (
                        <img src={img(game)} alt="" className="h-7 w-7 flex-shrink-0 rounded-md object-cover" />
                      ) : (
                        <span className="h-7 w-7 flex-shrink-0 rounded-md bg-dark-bg-secondary" />
                      )}
                      <span className="flex-1 truncate font-medium text-light-typo-primary dark:text-dark-typo-primary">{game.name}</span>
                      {isSelected && <Check size={16} className="text-green" aria-hidden />}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
          <FieldError message={error} />
        </div>
      )}
    </Listbox>
  )
}

const ImageDrop = ({
  id,
  label,
  aspect,
  file,
  onChange,
}: {
  id: string
  label: string
  aspect: string
  file: Maybe<File>
  onChange: (file: File | null) => void
}) => {
  const hasFile = file instanceof File && file.size !== 0
  const url = useObjectUrl(hasFile ? file : null)

  return (
    <div className="relative">
      <label
        htmlFor={id}
        className={classNames(
          'group relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border border-dashed transition-colors',
          'border-light-border-primary dark:border-dark-border-primary hover:border-btn-fancy-primary-end/60 bg-light-bg-secondary dark:bg-dark-bg',
          'focus-within:ring-2 focus-within:ring-btn-fancy-primary-end',
          aspect,
        )}
      >
        {url ? (
          <>
            <img src={url} alt={`${label} preview`} className="absolute inset-0 h-full w-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
              Replace {label.toLowerCase()}
            </span>
          </>
        ) : (
          <span className="flex flex-col items-center gap-1 px-2 text-center text-light-typo-gray dark:text-dark-typo-gray">
            <ImagePlus size={20} aria-hidden />
            <span className="text-xs font-semibold">Add {label.toLowerCase()}</span>
          </span>
        )}
        <input
          id={id}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const picked = e.target.files?.[0]
            if (picked) onChange(picked)
            e.target.value = ''
          }}
        />
      </label>
      {url && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Remove ${label.toLowerCase()}`}
          className={classNames('absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1 text-white hover:bg-black', FOCUS_RING)}
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  )
}
